import React from 'react'
import clsx from 'clsx'
import { Send } from 'iconsax-react'
import { useTheme } from '@mui/material'

import MentionInput from '../../molecules/Mention'
import Button from '../Button2'
import SendControl from '../../molecules/SendControl'

import { useEnterMode } from '../../../providers/EnterModeProvider'
import { useTaskCommentsEdit } from '../../../providers/TaskCommentEditProvider'
import useStyles from './styles'

type ButtonElementProps = {
  useHTMLButton: boolean
  onClick: () => void
  disabled: boolean
  className: string
  children?: React.ReactNode
}

const ButtonElement: React.FC<ButtonElementProps> = ({
  useHTMLButton,
  ...props
}) => {
  if (useHTMLButton) {
    return <button {...props} />
  }

  return <Button {...props} />
}

type TaskCommentInputProps = {
  value: string
  onChange: (s: string) => void
  canComment: boolean
  disabled?: boolean
  submit: () => void
  className?: string
  useHTMLButton?: boolean
  hideAdd?: boolean
  children?: React.ReactNode
  autoFocus?: boolean
  noPortalHost?: boolean
}

const TaskCommentInput: React.FC<TaskCommentInputProps> = ({
  value = '',
  onChange,
  canComment,
  disabled,
  submit,
  className,
  useHTMLButton,
  hideAdd,
  children,
  autoFocus = false,
  noPortalHost = false
}) => {
  const theme = useTheme()
  const { sendOnEnter, temp, enableTemp, resetTemp } = useEnterMode()
  const { editing, editingID, content, saveEdit, discardEdit, updateContent } =
    useTaskCommentsEdit()

  const fieldDisabled = React.useMemo(
    () => disabled || !canComment,
    [disabled, canComment]
  )

  const buttonDisabled = React.useMemo(
    () => fieldDisabled || (editing ? content : value).length === 0,
    [value, fieldDisabled, editing, content]
  )

  const containerRef = React.useRef<HTMLDivElement>(null)

  const classes = useStyles()

  // Scroll to input when editing
  React.useEffect(() => {
    if (!editing) {
      return
    }

    if (!containerRef.current) {
      return
    }

    containerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [editing, editingID])

  const checkSubmit = React.useCallback(
    e => {
      const { key, shiftKey } = e

      if (key === 'Enter') {
        // Disable send on enter when shift is held once
        if (shiftKey) {
          enableTemp()
          return
        }

        // Prevent sending when disabled
        if (!sendOnEnter || temp) {
          return
        }

        e.preventDefault()
        resetTemp()
        if (editing) {
          saveEdit()
        } else {
          submit()
        }
      }

      if (key === 'Escape') {
        discardEdit()
      }
    },
    [
      submit,
      enableTemp,
      resetTemp,
      sendOnEnter,
      temp,
      editing,
      saveEdit,
      discardEdit
    ]
  )

  return (
    <div
      ref={containerRef}
      className={clsx(classes.root, className)}
      // Padding when scrolling
      style={{ scrollMarginBlockStart: '71px' }}
    >
      {children}

      {/* @ts-ignore */}
      <MentionInput
        autoFocus={autoFocus}
        focus={editing}
        value={editing ? content : value}
        onChange={e => {
          const value = e.target.value
          if (editing) {
            updateContent(value)
          } else {
            onChange(value)
          }
        }}
        onKeyDown={checkSubmit}
        placeholder={
          editing
            ? 'Edit comment...'
            : canComment
            ? 'Enter comment...'
            : "You don't have the permissions to comment on this task"
        }
        disabled={fieldDisabled}
        checkAdd={false}
        className={clsx(classes.input, className)}
        inputClass='text'
        inputProps={{ maxLength: 2000 }}
        noPortalHost={noPortalHost}
      />

      <div className={classes.buttonHolder}>
        <ButtonElement
          useHTMLButton={useHTMLButton}
          onClick={() => {
            resetTemp()
            if (editing) {
              saveEdit()
            } else {
              submit()
            }
          }}
          disabled={buttonDisabled}
          className={clsx(
            useHTMLButton ? classes.htmlButton : classes.button,
            classes.selfBaseline
          )}
        >
          {!hideAdd && (editing ? 'EDIT' : 'ADD')}

          <Send
            variant='Bold'
            size={theme.size.base}
            className={classes.icon}
          />
        </ButtonElement>

        <SendControl />
      </div>
    </div>
  )
}

export default TaskCommentInput
